import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './HowToMeasure.css';

const HowToMeasure = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [measurementMode, setMeasurementMode] = useState('body'); // 'body' or 'readymade'
  const navigate = useNavigate();
  const location = useLocation();
  const { returnTo, productName } = location.state || {};

  const openImageModal = (imageSrc, imageAlt) => {
    setSelectedImage({ src: imageSrc, alt: imageAlt });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleStartCustomOrder = () => {
    // If user came from a product page, return there
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate('/collections');
    }
  };

  const handleReturnToProduct = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(-1); // Go back in browser history
    }
  };



  const measurementTypes = [
    // UPPERWEAR MEASUREMENTS (Coat, Jacket, Shirt)
    {
      id: 'neck',
      title: 'Neck',
      description: 'Measure the circumference of your neck at the height where your collar would sit. To be more accurate, place the tape measure around your neck and ensure it\'s not too tight or too loose.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/neck/desktop/2025-09-01/neck_instruction_1756722305218.webm'
    },
    {
      id: 'shoulder',
      title: 'Shoulders',
      description: 'Measure across the top of the back, following the natural curvature of the upper back from the tip of one shoulder to the tip of the other. It shouldn\'t be a perfectly straight line. If you need clarification on the shoulder tips, you can measure between the shoulder seams of a well-fitting shirt.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/shoulder/desktop/2025-09-01/shoulder_instruction_1756722309693.webm'
    },
    {
      id: 'chest',
      title: 'Chest',
      description: 'Stand up straight and position the measuring tape around the nipple level. Wrap it under the armpits and over the shoulder blades. This measurement represents the circumference of your chest at its widest point.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/chest/desktop/2025-09-01/chest_instruction_1756722295228.webm'
    },
    {
      id: 'sleeve',
      title: 'Sleeve Length',
      description: 'With your arm relaxed at your side, measure from where the shoulder seam meets the crown (highest point of the shoulder) down to your desired length, typically at the base of the thumb where it meets your wrist.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/sleeve/desktop/2025-09-01/sleeve_instruction_1756722311835.webm'
    },
    {
      id: 'coatLength',
      title: 'Coat Length',
      description: 'Measure from the base of your neck (where your collar would sit) down to your desired coat length. For a standard suit jacket, this typically ends around the middle of your hand when your arms are at your sides.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/coatLength/desktop/2025-09-01/coatLength_instruction_1756722298019.webm'
    },
    
    // BOTTOMWEAR MEASUREMENTS (Pants, Trousers)
    {
      id: 'waist',
      title: 'Waist',
      description: 'Measure around your waist at the height you prefer to wear your pants, which may differ from your natural waistline. Avoid pulling the tape measure too tightly. If you\'re unsure, wear trousers that fit well and measure around the waistband where they sit.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/waist/desktop/2025-09-01/waist_instruction_1756722316420.webm'
    },
    {
      id: 'hip',
      title: 'Hips',
      description: 'Measure around the fullest part of your hips and seat (bum). Stand with your feet hip-width apart, empty pockets, and ensure the tape is level and untwisted. You should feel the tape lightly against your body.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/hip/desktop/2025-09-01/hip_instruction_1756722300917.webm'
    },
    {
      id: 'thigh',
      title: 'Thigh',
      description: 'Please measure the circumference of your thigh at its widest point, usually near the top of the inseam, but it may vary slightly depending on your physique.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/thigh/desktop/2025-09-01/thigh_instruction_1756722314001.webm'
    },
    {
      id: 'pantLength',
      title: 'Trouser Length/Out-seam',
      description: 'Measure vertically from the top of where you want the trouser waist to sit along the side pant seam to the desired trouser length, typically about 3cm (1.18 inches) above the ground. To assist, you can wear a pair of well-fitting trousers and shoes similar to those you intend to wear with the suit.',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/pantLength/desktop/2025-09-01/pantLength_instruction_1756722307386.webm'
    },
    {
      id: 'inseam',
      title: 'Trouser Length/In-seam',
      description: 'We typically use the out-seam measurement you provide for our trousers, but in some cases, the inseam measurement can be helpful. Again, wearing well-fitting trousers and similar shoes can help. Measure vertically from the bottom of the trouser (usually 3cm from the floor) up the inside seam to the desired height of the crotch. In Made to Order/standard store-bought trousers, these are typically 30" (short), 32" (regular), or 34" (long).',
      gif: 'https://images.vastrebyvijh.com/vastre-images/measurement-instructions/inseam/desktop/2025-09-01/inseam_instruction_1756722302969.webm'
    }
  ];

  const readymadeMeasurementTypes = [
    // UPPERWEAR MEASUREMENTS (Coat, Jacket, Shirt)
    {
      id: 'coatlength',
      title: 'Coat Length',
      description: 'To measure a coat length, lay it flat with the back facing up. Measure from the base of the collar seam to the bottom hem for the total length.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/1_coatlength_658b9c3b-480a-4238-96d6-7f78d9713260_1756725389446_0.webp'
    },
    {
      id: 'sleeve_length',
      title: 'Sleeve Length',
      description: 'Measure from the shoulder seam to the desired sleeve length, typically at the wrist or slightly above.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/2_sleeve_length_32ce71fc-fbe4-413b-874d-f1a3763320db_1756725401539_1.webp'
    },
    {
      id: 'shoulder',
      title: 'Shoulder',
      description: 'Measure across the shoulders from one shoulder seam to the other, following the natural curve of the shoulders.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/3_shoulder_8fe766b7-6cb6-4ed1-93ea-88ef5c9ccd40_1756725410765_2.webp'
    },
    {
      id: 'chest',
      title: 'Chest',
      description: 'To measure the chest width, lay the jacket flat with buttons fastened and measure across from underarm seam to seam. Double this for the full chest measurement.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/4_chest_e8867943-7e34-4d15-a977-7d1dfb75045f_1756725418774_3.webp'
    },
    {
      id: 'belly',
      title: 'Belly/Waist',
      description: 'Lay the jacket flat, fasten the buttons, and measure across the narrowest part of the waist. Double this for the full waist circumference.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/5_belly_234a5f13-591e-478a-b73c-57dead7d5678_1756725427792_4.webp'
    },
    {
      id: 'hip',
      title: 'Hip',
      description: 'Measure across the widest part of the hips on the garment, typically around the seat area.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/6_hip_2b52b6a5-2b39-4a31-a62e-b9b7afb6a961_1756725434669_5.webp'
    },
    
    // BOTTOMWEAR MEASUREMENTS (Pants, Trousers)
    {
      id: 'waist',
      title: 'Waist',
      description: 'Measure across the waistband of the pants and double for the full waist circumference.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/11_waist_ca333bf0-21b6-4edb-ab45-97bc2f83662f_1756725467223_10.webp'
    },
    {
      id: 'hip_2',
      title: 'Hip (Pants)',
      description: 'Measure across the widest part of the pants in the hip area.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/12_hip_8c61c31c-d0e9-4ce2-b9ef-4142203f985d_1756725473643_11.webp'
    },
    {
      id: 'thigh',
      title: 'Thigh',
      description: 'To measure the thigh width, lay the pants flat and measure across the widest part below the crotch. Double the measurement for the full thigh circumference.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/10_thigh_25b106b5-9be8-4dda-a547-9ac440dabd40_1756725460593_9.webp'
    },
    {
      id: 'pant_length',
      title: 'Pant Length',
      description: 'To measure the pant length, lay the pants flat and measure from the top of the waistband to the bottom hem along the outer leg.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/7_pant_length_1df3fb3e-3645-4412-b463-099e259b2381_1756725441159_6.webp'
    },
    {
      id: 'inseam',
      title: 'Inseam',
      description: 'Measure from the crotch seam down the inside leg to the desired length.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/9_inseam_5d307ec8-5dfe-4242-8648-0da88b833c56_1756725453770_8.webp'
    },
    {
      id: 'bottom_opening',
      title: 'Bottom Opening',
      description: 'Measure the circumference of the pant leg opening at the bottom hem.',
      image: 'https://images.vastrebyvijh.com/vastre-images/measurement-readymade/desktop/2025-09-01/8_bottom_opening_e9a5bc93-f5ae-4125-ba98-1ead941eb3e2_1756725447579_7.webp'
    }
  ];

  const currentMeasurementTypes = measurementMode === 'body' ? measurementTypes : readymadeMeasurementTypes;

  return (
    <div className="how-to-measure-page">
      {/* Return to Product Banner - Show when user came from product page */}
      {returnTo && (
        <div className="return-to-product-banner">
          <div className="return-banner-content">
            <button 
              className="return-to-product-btn"
              onClick={handleReturnToProduct}
              aria-label="Return to product page"
            >
              <span className="return-arrow">←</span>
              <span className="return-text">
                Back to Product
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="measurement-hero">
        <div className="measurement-hero-background">
          <div className="measurement-hero-pattern"></div>
        </div>
        
        <div className="measurement-hero-container">
          <div className="measurement-hero-content">
            <div className="measurement-hero-text-section">
              <h1 className="measurement-hero-title">PERFECT FIT GUARANTEED</h1>
              <p className="measurement-hero-subtitle">Master the art of precise measurements for bespoke excellence</p>
            </div>
            
            <div className="measurement-hero-visual-section">
              <div className="measurement-hero-image-container">
                <picture>
                  <source 
                    media="(min-width: 1920px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-hero/desktop/2025-09-01/measurement-hero_desktop_1756721889945_0_1920w.webp"
                  />
                  <source 
                    media="(min-width: 1440px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-hero/desktop/2025-09-01/measurement-hero_desktop_1756721889945_0_1440w.webp"
                  />
                  <source 
                    media="(min-width: 1080px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-hero/desktop/2025-09-01/measurement-hero_desktop_1756721889945_0_1080w.webp"
                  />
                  <source 
                    media="(min-width: 768px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-hero/desktop/2025-09-01/measurement-hero_desktop_1756721889945_0_768w.webp"
                  />
                  <source 
                    media="(min-width: 480px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-hero/desktop/2025-09-01/measurement-hero_desktop_1756721889945_0_480w.webp"
                  />
                  <img 
                    src="https://images.vastrebyvijh.com/vastre-images/measurement-hero/desktop/2025-09-01/measurement-hero_desktop_1756721889945_0.webp"
                    alt="Measurement Guide Hero" 
                    className="measurement-hero-image"
                  />
                </picture>
              </div>
            </div>
          </div>
          
          <div className="measurement-hero-actions">
            <div className="measurement-hero-buttons">
              <button 
                className={`measurement-hero-btn ${measurementMode === 'body' ? 'primary-btn' : 'secondary-btn'}`}
                onClick={() => {
                  setMeasurementMode('body');
                  document.getElementById('measurement-instructions').scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span className="btn-text">BODY MEASUREMENTS</span>
                <span className="btn-icon">→</span>
              </button>
              <button 
                className={`measurement-hero-btn ${measurementMode === 'readymade' ? 'primary-btn' : 'secondary-btn'}`}
                onClick={() => {
                  setMeasurementMode('readymade');
                  document.getElementById('measurement-instructions').scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span className="btn-text">READY MADE MEASUREMENTS</span>
                <span className="btn-icon">→</span>
              </button>
            </div>
            
            <div className="measurement-hero-scroll-indicator">
              <div className="scroll-arrow" onClick={() => document.getElementById('measurement-instructions').scrollIntoView({ behavior: 'smooth' })}>
                <span>Explore Guide</span>
                <div className="arrow-icon">↓</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="measurement-main">
        <div className="measurement-container">


          <div className="measurement-content">
            {/* Left Column - Body Diagram */}
            <div className="measurement-diagram-section">
              <div className="measurement-diagram-container">
                <picture>
                  <source 
                    media="(min-width: 1920px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722070608_0_1920w.webp"
                  />
                  <source 
                    media="(min-width: 1440px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722070608_0_1440w.webp"
                  />
                  <source 
                    media="(min-width: 1080px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722070608_0_1080w.webp"
                  />
                  <source 
                    media="(min-width: 768px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722070608_0_768w.webp"
                  />
                  <source 
                    media="(min-width: 480px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722070608_0_480w.webp"
                  />
                  <img 
                    src="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722070608_0.webp"
                    alt="Body Measurement Diagram" 
                    className="measurement-diagram"
                    onClick={() => openImageModal('https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722070608_0.webp', 'Body Measurement Diagram')}
                  />
                </picture>
              </div>
            </div>

            {/* Right Column - Measurement List */}
            <div className="measurement-list-section">
              <div className="measurement-list-container">
                <picture>
                  <source 
                    media="(min-width: 1920px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722083961_1_1920w.webp"
                  />
                  <source 
                    media="(min-width: 1440px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722083961_1_1440w.webp"
                  />
                  <source 
                    media="(min-width: 1080px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722083961_1_1080w.webp"
                  />
                  <source 
                    media="(min-width: 768px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722083961_1_768w.webp"
                  />
                  <source 
                    media="(min-width: 480px)" 
                    srcSet="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722083961_1_480w.webp"
                  />
                  <img 
                    src="https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722083961_1.webp"
                    alt="Measurement Chart" 
                    className="measurement-list-image"
                    onClick={() => openImageModal('https://images.vastrebyvijh.com/vastre-images/measurement-diagrams/desktop/2025-09-01/measurement-diagrams_desktop_1756722083961_1.webp', 'Measurement Chart')}
                  />
                </picture>
              </div>
            </div>
          </div>

          {/* Measurement Instructions */}
          <div className="measurement-instructions" id="measurement-instructions">
            <h3 className="measurement-instructions-title">
              {measurementMode === 'body' ? 'Detailed Body Measurement Instructions' : 'Ready Made Measurement Instructions'}
            </h3>
            
            <div className="measurement-instructions-grid">
              {currentMeasurementTypes.map((measurement) => (
                <div key={measurement.id} className="measurement-instruction-card">
                  <div className="measurement-instruction-header">
                    <h4 className="measurement-instruction-title">{measurement.title}</h4>
                  </div>
                  <div className="measurement-instruction-content">
                    <div className="measurement-instruction-text">
                      <p>{measurement.description}</p>
                    </div>
                    <div className="measurement-instruction-visual">
                      <img 
                        src={measurementMode === 'body' ? measurement.gif : measurement.image}
                        alt={`How to measure ${measurement.title.toLowerCase()}`}
                        className="measurement-instruction-gif"
                        onError={(e) => console.error('Image error for:', measurement.title, e)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="measurement-cta">
            <div className="measurement-cta-content">
              <h3 className="measurement-cta-title">
                {returnTo ? 'Ready to Continue?' : 'Ready to Get Started?'}
              </h3>
              <p className="measurement-cta-text">
                {returnTo 
                  ? 'Return to the product page and complete your measurements' 
                  : 'Take your measurements and create your perfect custom outfit'}
              </p>
              <button 
                className="measurement-cta-button"
                onClick={handleStartCustomOrder}
              >
                <span className="btn-text">
                  {returnTo ? 'RETURN TO PRODUCT' : 'START CUSTOM ORDER'}
                </span>
                <span className="btn-icon">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>×</button>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.alt} 
              className="image-modal-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HowToMeasure;
